/**
 * useQuoteCharge — a cobrança Pix de UM orçamento: ler e emitir.
 *
 * ─── ONLINE-ONLY, E ISSO É DA NATUREZA DA COISA ─────────────────────────────
 *
 * Emitir cobrança é uma chamada ao Asaas: não existe versão offline dela, e
 * enfileirar seria pior do que recusar — o prestador acharia que mandou o QR e
 * o cliente receberia um documento sem forma de pagamento. A mutation declara
 * `networkMode: 'offlineFirst'` justamente para FALHAR RÁPIDO com aviso: com o
 * default `'online'` o TanStack pausaria ao perder a rede com a tela aberta
 * (#41) e o botão ficaria em "Gerando…" para sempre.
 *
 * ─── ORDEM: LANÇAMENTO PRIMEIRO, COBRANÇA DEPOIS ────────────────────────────
 *
 * O webhook só dá baixa no financeiro se a cobrança carregar `finance_entry_id`
 * (ver `asaas-webhook`). Então o lançamento precisa existir ANTES.
 *
 * A ordem inversa parece mais segura e não é: se a cobrança nascesse primeiro e
 * o lançamento falhasse, o cliente poderia PAGAR uma cobrança que nunca daria
 * baixa — a promessa central da feature quebrada em silêncio. Do jeito certo, o
 * pior caso é um "a receber" sem cobrança, que é um dado verdadeiro (o dinheiro
 * é devido) e que o usuário vê e pode apagar.
 *
 * E o lançamento é REAPROVEITADO quando já existe um para o orçamento — inclusive
 * o que o próprio usuário criou pelo convite do financeiro. Criar outro geraria
 * receita em dobro no fluxo de caixa, sem nenhum erro na tela.
 */
import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { checkNetworkStatus } from '@/hooks/useNetworkStatus';
import { useCreateFinanceEntry, notifyFinanceEntriesChanged } from '@/hooks/useFinance';
import { supabase } from '@/integrations/supabase/client';
import {
  OPEN_CHARGE_POLL_MS,
  pickCurrentCharge,
  pickPayableCharge,
  shouldPollCharges,
  toPdfPixDynamic,
  type AsaasChargeLike,
} from '@/lib/asaasCharge';
import {
  PAYOUT_POLL_MS,
  payoutRetryMessage,
  pickCurrentTransfer,
  resolvePayoutView,
  shouldPollPayout,
  type AsaasTransferLike,
} from '@/lib/asaasPayout';
import { todayIsoDate } from '@/lib/financeStatus';

const KEY = 'asaas-charges';
const TRANSFERS_KEY = 'asaas-transfers';

/**
 * O corpo de uma resposta NÃO-2xx do `functions.invoke`.
 *
 * O supabase-js embrulha o erro e o corpo fica só no `context` (um `Response`).
 * Sem ler dele, um 409 com `reason: 'no_payout_key'` vira um "erro" genérico e o
 * usuário nunca descobre que basta cadastrar a chave.
 */
async function readInvokeErrorBody(error: unknown): Promise<unknown> {
  const response = (error as { context?: unknown } | null)?.context;
  if (!(response instanceof Response)) return null;
  try {
    return await response.clone().json();
  } catch {
    return null;
  }
}

export interface QuoteChargeRow extends AsaasChargeLike {
  id: string;
  status: string;
  value: number | string;
  invoice_url: string | null;
  finance_entry_id: string | null;
  last_error: string | null;
  created_at: string;
}

export interface CreateQuoteChargeInput {
  quoteId: string;
  /** O total do documento. É ele que o cliente vai pagar. */
  value: number;
  description: string;
  clientId?: string | null;
  client?: { name?: string | null; cpfCnpj?: string | null; email?: string | null; phone?: string | null };
}

/** Erro tratado, com mensagem própria — distingue-se de falha inesperada. */
class ChargeError extends Error {}

export function useQuoteCharge(quoteId: string | null | undefined) {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const queryClient = useQueryClient();
  const createFinanceEntry = useCreateFinanceEntry();

  // Id temporário de criação offline nunca vai ao Postgres (#6): a consulta
  // simplesmente não roda, e a tela cai no estado "sem cobrança".
  const realQuoteId = quoteId && !String(quoteId).startsWith('offline_') ? quoteId : null;

  const query = useQuery({
    queryKey: [KEY, realQuoteId],
    enabled: Boolean(user?.id && companyId && realQuoteId),
    networkMode: 'offlineFirst',
    staleTime: 30 * 1000,
    refetchOnMount: 'always',
    // Quem paga é o cliente e quem grava é o webhook: sem reperguntar, o card
    // fica em "Aguardando" com o dinheiro já na conta. Só enquanto há cobrança
    // em aberto — ver `shouldPollCharges`.
    refetchInterval: (query) =>
      shouldPollCharges(query.state.data as QuoteChargeRow[] | undefined)
        ? OPEN_CHARGE_POLL_MS
        : false,
    retry: 1,
    queryFn: async (): Promise<QuoteChargeRow[]> => {
      const { data, error } = await supabase
        .from('asaas_charges' as never)
        .select('id, status, value, pix_payload, pix_qr_base64, invoice_url, environment, finance_entry_id, last_error, created_at')
        .eq('quote_id', realQuoteId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data as unknown as QuoteChargeRow[]) ?? [];
    },
  });

  const charges = query.data ?? [];

  // Duas perguntas diferentes, duas funções (ver `asaasCharge.ts`): o PAPEL só
  // aceita cobrança em aberto; a TELA precisa mostrar também a paga e a que
  // falhou.
  const payable = useMemo(() => pickPayableCharge(charges), [charges]);
  const current = useMemo(() => pickCurrentCharge(charges), [charges]);
  // A cobrança CORRENTE, não a pagável: o documento precisa saber que existe uma
  // cobrança JÁ PAGA para não cair no QR estático e convidar a pagar de novo.
  // Quem recusa o QR de uma cobrança quitada é `pixQrEligible`, não este mapa.
  const pixDynamic = useMemo(() => toPdfPixDynamic(current), [current]);

  // ── O REPASSE ────────────────────────────────────────────────────────────
  // Consulta PRÓPRIA e só habilitada quando existe cobrança liquidada: sem o
  // gate, toda tela de orçamento faria uma requisição a mais para descobrir que
  // não há repasse nenhum. `received` é o único status que gera repasse (o
  // dinheiro precisa estar no saldo do Asaas).
  const settledChargeId = current && current.status === 'received' ? current.id : null;

  const transfersQuery = useQuery({
    queryKey: [TRANSFERS_KEY, settledChargeId],
    enabled: Boolean(user?.id && companyId && settledChargeId),
    networkMode: 'offlineFirst',
    staleTime: 30 * 1000,
    refetchOnMount: 'always',
    // A outra perna do mesmo dinheiro muda sozinha logo depois (14s no E2E):
    // `requested` → `authorized` → `done`. Termina sempre — ver `shouldPollPayout`.
    refetchInterval: (query) =>
      shouldPollPayout(
        query.state.data as AsaasTransferLike[] | undefined,
        query.state.dataUpdateCount,
      )
        ? PAYOUT_POLL_MS
        : false,
    retry: 1,
    queryFn: async (): Promise<AsaasTransferLike[]> => {
      const { data, error } = await supabase
        .from('asaas_transfers' as never)
        .select('id, status, value, last_error, created_at, completed_at')
        .eq('charge_id', settledChargeId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data as unknown as AsaasTransferLike[]) ?? [];
    },
  });

  const payout = useMemo(
    () =>
      resolvePayoutView({
        chargeStatus: current?.status,
        // `undefined` enquanto a consulta não respondeu: `resolvePayoutView`
        // trata `null` como `missing` (pendência), e mostrar isso durante o
        // carregamento seria alarme falso — "tem dinheiro parado" toda vez que a
        // tela abrisse.
        transfer: transfersQuery.isSuccess ? pickCurrentTransfer(transfersQuery.data) : undefined,
      }),
    [current?.status, transfersQuery.isSuccess, transfersQuery.data],
  );

  const retryPayout = useMutation({
    networkMode: 'offlineFirst' as const,
    mutationFn: async () => {
      if (!settledChargeId) throw new ChargeError('Não há pagamento a repassar.');
      if (!(await checkNetworkStatus())) throw new ChargeError('offline');

      const { data, error } = await supabase.functions.invoke('asaas-payout-retry', {
        body: { chargeId: settledChargeId },
      });
      // A função responde 409 com `{ requested: false, reason }`, e o
      // `functions.invoke` do supabase-js trata não-2xx como erro — então o
      // motivo viria PERDIDO se olhássemos só o `error`. Quem tem o veredito é
      // o corpo (#9): sem ler `reason`, o usuário receberia "erro" onde a
      // resposta certa é "cadastre sua chave Pix".
      const body = (data ?? (await readInvokeErrorBody(error))) as
        | { requested?: boolean; reason?: string; code?: string }
        | null;

      if (!body) throw new ChargeError('Não foi possível pedir o repasse agora.');
      // `reason ?? code`: a função devolve `reason` só no ramo do `requestPayout`
      // — os demais (`not_payable`, `forbidden`, `charge_not_found`) devolvem
      // `code`. Lendo só `reason`, todos eles caíam na mensagem genérica, e o
      // prestador não descobria que o problema era permissão, não instabilidade.
      if (!body.requested) throw new ChargeError(payoutRetryMessage(body.reason ?? body.code));
      return body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [TRANSFERS_KEY, settledChargeId] });
      toast.success(payoutRetryMessage('requested'));
    },
    onError: (error: Error) => {
      if (error instanceof ChargeError) {
        if (error.message === 'offline') {
          toast.warning('Pedir o repasse exige conexão. Conecte-se e tente de novo.');
          return;
        }
        toast.warning(error.message);
        return;
      }
      toast.error('Não foi possível pedir o repasse agora.');
    },
  });

  const findExistingEntryId = useCallback(async (): Promise<string | null> => {
    if (!companyId || !realQuoteId) return null;
    const { data, error } = await supabase
      .from('finance_entries')
      .select('id')
      .eq('quote_id', realQuoteId)
      .eq('company_id', companyId)
      .eq('kind', 'income')
      .order('created_at', { ascending: true })
      .limit(1);
    // Falhar a BUSCA não pode virar "não existe": criaria um segundo lançamento
    // e a receita apareceria em dobro (#150 — `data` nulo por erro e `data`
    // vazio por ausência não podem cair no mesmo ramo).
    if (error) throw new ChargeError('Não foi possível conferir o financeiro. Tente de novo.');
    return (data?.[0] as { id?: string } | undefined)?.id ?? null;
  }, [companyId, realQuoteId]);

  const create = useMutation({
    networkMode: 'offlineFirst' as const,
    mutationFn: async (input: CreateQuoteChargeInput) => {
      if (!user?.id) throw new ChargeError('Sem sessão.');
      if (!companyId) throw new ChargeError('Nenhuma empresa ativa.');
      if (!realQuoteId) throw new ChargeError('Salve o orçamento antes de gerar a cobrança.');
      if (!(await checkNetworkStatus())) throw new ChargeError('offline');
      if (!(input.value > 0)) throw new ChargeError('O orçamento precisa ter um valor para ser cobrado.');

      // PRÉ-CHECAGEM do documento do pagador. O Asaas EXIGE CPF/CNPJ para criar
      // cobrança Pix, mas o orçamento não exige — e é assim que deve ser, o
      // documento do cliente é opcional no app. Sem esta checagem o pedido sai,
      // o Asaas recusa com `invalid_object` e o prestador lê "não foi possível
      // gerar a cobrança": a informação acionável ("preencha o CPF do cliente")
      // existe e morre no log. Achado no primeiro E2E de sandbox.
      //
      // Checar AQUI, e não só traduzir a recusa, também evita deixar um cliente
      // órfão criado no Asaas a cada tentativa fracassada.
      const documento = (input.client?.cpfCnpj ?? '').replace(/\D/g, '');
      if (documento.length !== 11 && documento.length !== 14) {
        throw new ChargeError(
          'Informe o CPF ou CNPJ do cliente no orçamento — o banco exige isso para gerar a cobrança Pix.',
        );
      }

      // 1) O lançamento — reaproveitado quando já existe (ver o cabeçalho).
      let financeEntryId = await findExistingEntryId();
      if (!financeEntryId) {
        const created = await createFinanceEntry.mutateAsync({
          kind: 'income',
          description: input.description,
          amount: input.value,
          due_date: todayIsoDate(),
          status: 'pending',
          client_id: input.clientId ?? null,
          quote_id: realQuoteId,
          company_id: companyId,
        });
        // O ramo offline devolve `{ offline: true }` sem id. Aqui ele é
        // inalcançável (o `checkNetworkStatus` acima já barrou), mas prosseguir
        // sem id criaria uma cobrança que o webhook não consegue conciliar.
        financeEntryId = 'id' in created ? created.id : null;
        if (!financeEntryId) throw new ChargeError('Não foi possível registrar o recebimento.');
      }

      // 2) A cobrança. `dueDate` = hoje: o Pix não tem prazo de compensação, e
      // uma data futura só faria a cobrança nascer sem poder ser paga.
      const { data, error } = await supabase.functions.invoke('asaas-charge', {
        body: {
          companyId,
          quoteId: realQuoteId,
          financeEntryId,
          value: input.value,
          dueDate: todayIsoDate(),
          description: input.description,
          client: { id: input.clientId ?? undefined, ...input.client },
        },
      });

      // 2xx não é sucesso quando o corpo carrega o veredito (#9): a função
      // devolve `code` quando recusa, e `pixError` quando a cobrança nasceu mas
      // ficou SEM QR — que é um orçamento que o cliente não consegue pagar.
      if (error) throw new ChargeError('Não foi possível gerar a cobrança.');
      const result = data as
        | { code?: string; message?: string; pixError?: string | null; pixPayload?: string | null }
        | null;
      // A mensagem do provedor entra como ÚLTIMO recurso: ela vem em português e
      // costuma dizer exatamente o que falta ("é necessário preencher o CPF..."),
      // enquanto o texto genérico manda o prestador ao suporte sem pista nenhuma.
      if (result?.code) throw new ChargeError(chargeErrorMessage(result.code, result.message));
      if (!result?.pixPayload) {
        throw new ChargeError(
          'A cobrança foi criada, mas o código Pix não veio. Confira a conta de recebimento e tente de novo.',
        );
      }

      return result;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY, realQuoteId] });
      // O lançamento novo precisa alcançar a trilha do trabalho e o convite do
      // financeiro, que têm queries próprias (#121, addendum).
      notifyFinanceEntriesChanged();
      toast.success('Cobrança Pix gerada. O orçamento já sai com o QR.');
    },
    onError: (error: Error) => {
      if (error instanceof ChargeError) {
        if (error.message === 'offline') {
          toast.warning('Gerar cobrança exige conexão. Conecte-se e tente de novo.');
          return;
        }
        toast.warning(error.message);
        return;
      }
      toast.error('Não foi possível gerar a cobrança.');
    },
  });

  return {
    /** Estado do REPASSE ao prestador — a metade do dinheiro que era invisível. */
    payout,
    payoutLoading: transfersQuery.isLoading,
    retryingPayout: retryPayout.isPending,
    retryPayout: retryPayout.mutateAsync,
    charges,
    /** A que vai ao documento (só em aberto). */
    payable,
    /** A mais relevante para a TELA (inclui paga e falhada). */
    current,
    /** Já no formato do gerador de PDF. */
    pixDynamic,
    loading: query.isLoading,
    creating: create.isPending,
    createCharge: create.mutateAsync,
  };
}

/** Os códigos que o `asaas-charge` devolve, em português e acionáveis. */
function chargeErrorMessage(code: string, providerMessage?: string): string {
  switch (code) {
    case 'forbidden':
      return 'Você não tem permissão para cobrar por esta empresa.';
    case 'account_not_found':
    case 'account_not_approved':
      return 'Sua conta de recebimento ainda não está liberada para cobrar.';
    case 'invalid_value':
      return 'O valor do orçamento não pode ser cobrado.';
    default:
      return (
        providerMessage?.trim() ||
        'Não foi possível gerar a cobrança. Tente de novo em alguns instantes.'
      );
  }
}
